import { useCallback, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useAddSeeds } from '@/hooks/useSeeds';
import type { SeedWizardFormData } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';
import type { SeedTypeOption } from '@/types';

/**
 * Lifecycle status for a single submit stage.
 *  - idle: not started yet (or not applicable)
 *  - skipped: stage will not run for this submission (e.g. no creds configured)
 *  - pending: in flight
 *  - success: completed cleanly
 *  - error: failed
 */
export type SubmitStageStatus =
  | 'idle'
  | 'skipped'
  | 'pending'
  | 'success'
  | 'error';

interface UseAddSeedSubmitProps {
  formData: SeedWizardFormData;
  onSuccess: (seedType: SeedTypeOption, textInput: string) => void;
}

interface UseAddSeedSubmitReturn {
  handleSubmit: () => Promise<void>;
  /** True from submit click until both stages settle. */
  isPending: boolean;
  /** True once submit has run to completion (success or error). */
  isComplete: boolean;
  seedsStatus: SubmitStageStatus;
  credentialsStatus: SubmitStageStatus;
  /** First error surfaced during submit, if any. */
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for handling seed submission in the AddSeedByTypeModal.
 *
 * Drives a two-stage progress UI: stage 1 adds the seeds, stage 2 attaches
 * web-auth credentials (skipped when none configured).
 */
export function useAddSeedSubmit({
  formData,
  onSuccess,
}: UseAddSeedSubmitProps): UseAddSeedSubmitReturn {
  const axios = useAxios();
  const { mutateAsync: bulkAddSeeds } = useAddSeeds();

  // Local pending state covers the full submit lifecycle (seeds add + per-seed
  // broker POSTs). bulkAddSeeds.isPending flips back to false the moment the
  // seeds mutation resolves, but the wizard keeps running async work after
  // that — relying on it alone re-enables the Submit button mid-flight and
  // lets users double-submit, which creates duplicate credential nodes
  // (one HAS_CREDENTIAL edge per click).
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [seedsStatus, setSeedsStatus] = useState<SubmitStageStatus>('idle');
  const [credentialsStatus, setCredentialsStatus] =
    useState<SubmitStageStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setIsSubmitting(false);
    setIsComplete(false);
    setSeedsStatus('idle');
    setCredentialsStatus('idle');
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.seedType || isSubmitting) return;

    const parsedSeeds = formData.textInput
      ? formData.textInput
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
      : [];

    if (parsedSeeds.length === 0) return;

    const nonEmptyHeaders =
      formData.authMode === 'static-headers'
        ? formData.authHeaders.filter(h => h.key && h.value)
        : [];
    const willAttachCreds = nonEmptyHeaders.length > 0;

    setIsSubmitting(true);
    setIsComplete(false);
    setError(null);
    setSeedsStatus('pending');
    setCredentialsStatus(willAttachCreds ? 'idle' : 'skipped');

    // Stage 1: add seeds.
    try {
      await bulkAddSeeds({
        seeds: parsedSeeds,
        type: formData.seedType,
      });
      setSeedsStatus('success');
    } catch (err) {
      setSeedsStatus('error');
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsSubmitting(false);
      setIsComplete(true);
      return;
    }

    // Stage 2: attach credentials (only when configured).
    if (willAttachCreds) {
      setCredentialsStatus('pending');
      try {
        const headersObj = Object.fromEntries(
          nonEmptyHeaders.map(h => [h.key, h.value])
        );
        for (const seed of parsedSeeds) {
          await axios.post('/broker', {
            Category: 'env-integration',
            Type: 'web-auth',
            Format: ['token'],
            Operation: 'add',
            ResourceKey: `#webapplication#${seed}`,
            Parameters: {
              label:
                formData.authLabel || nonEmptyHeaders[0]?.key || 'Static Token',
              method: 'static-token',
              headers: headersObj,
            },
          });
        }
        setCredentialsStatus('success');
      } catch (err) {
        setCredentialsStatus('error');
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }

    onSuccess(formData.seedType, formData.textInput);
    setIsSubmitting(false);
    setIsComplete(true);
  }, [formData, isSubmitting, bulkAddSeeds, axios, onSuccess]);

  return {
    handleSubmit,
    isPending: isSubmitting,
    isComplete,
    seedsStatus,
    credentialsStatus,
    error,
    reset,
  };
}
